// An account locked by failed attempts (423 locked), as the pages that meet one
// show it: a countdown to the moment it lifts, which goes away by itself when
// that moment comes.
//
// Three pages meet it. The sign-in page, where it answers a guess at a locked
// account; and the two that ask for the current password — the forced "choose
// your own password" page and Settings — because the server counts a current
// password the way it counts a sign-in: a wrong one adds to the same tally, and
// while the account is locked the password is not looked at, however right it
// is. Each page says it in its own words; this is only the clock.

import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../api'

/**
 * A lock the server reported: when the account answers again, in epoch
 * milliseconds — or null when the answer named no moment this computer's clock
 * has yet to reach, which is said plainly rather than shown as a stuck 0:00.
 */
export type Lock = { until: number | null }

/** The lock a refusal reports, or null when it is not one. */
export function lockOf(err: unknown): Lock | null {
  // By the status: 423 is only ever a locked account. The code alone is not
  // enough, since a lesson that is shut is refused with it too (as a 403).
  if (!(err instanceof ApiError) || err.status !== 423) return null
  const at = Date.parse(err.fields['locked_until'] ?? '')
  return { until: Number.isNaN(at) || at <= Date.now() ? null : at }
}

export type LockClock = {
  /** The lock on screen, or null. */
  lock: Lock | null
  /** Puts a lock on screen (the clock starts at once), or takes it off. */
  setLock: (next: Lock | null) => void
  /** Seconds until it lifts, ticking; 0 when there is nothing to count. */
  remaining: number
  /**
   * The wait as it stood when the lock was reported, for screen readers: the
   * visible clock ticks every second, and announcing each tick would be noise.
   */
  lockedFor: number
  /** Whether there is a wait to show, rather than "try again later". */
  counting: boolean
}

/** The countdown to the end of a lock, cleared when it reaches zero. */
export function useLockClock(): LockClock {
  const [lock, setLockState] = useState<Lock | null>(null)
  const [remaining, setRemaining] = useState(0)
  const [lockedFor, setLockedFor] = useState(0)

  const until = lock?.until ?? null
  useEffect(() => {
    if (until === null) {
      setRemaining(0)
      return
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((until - Date.now()) / 1000))
      setRemaining(left)
      // Over: the page is back to how it always was. A newer answer that has
      // replaced this lock in the meantime is left alone.
      if (left === 0) {
        window.clearInterval(timer)
        setLockState((l) => (l?.until === until ? null : l))
      }
    }
    const timer = window.setInterval(tick, 1000)
    tick()
    return () => window.clearInterval(timer)
  }, [until])

  const setLock = useCallback((next: Lock | null) => {
    if (next?.until != null) {
      const wait = Math.max(1, Math.ceil((next.until - Date.now()) / 1000))
      setLockedFor(wait)
      // Set with the lock, so the first frame already shows the wait.
      setRemaining(wait)
    }
    setLockState(next)
  }, [])

  return { lock, setLock, remaining, lockedFor, counting: lock !== null && lock.until !== null && remaining > 0 }
}

/** 14:59 — minutes and seconds, the way a countdown is read in every language here. */
export function clock(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
